/**
 * DnD Timetable Provider (Full React Conversion)
 * Wraps the timetable with drag-and-drop context and React state
 */
import {
    DndContext,
    DragOverlay,
    pointerWithin,
    rectIntersection,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { memo, useState, useCallback } from 'react';
import ReactTimetableContext from '../context/ReactTimetableContext';
import CourseBoxOverlay from './CourseBoxOverlay';
import { useReactTimetable } from '../hooks/useReactTimetable';
import { isValidSectionSwap } from '../utils/timetableActions';

const measuring = {
    droppable: {
        // Alternatives mount after drag start, so they must be measured immediately.
        strategy: MeasuringStrategy.Always,
    },
};

function detectSwapCollision(args) {
    const pointerCollisions = pointerWithin(args);
    return pointerCollisions.length > 0 ? pointerCollisions : rectIntersection(args);
}

const DndTimetableProvider = memo(({ children }) => {
    const [activeId, setActiveId] = useState(null);
    const [activeCourse, setActiveCourse] = useState(null);

    // Use React-based timetable state
    const timetableState = useReactTimetable();
    const { swapSection, showVirtualCourses, hideVirtualCourses } = timetableState;

    // Configure sensors for both mouse and touch
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 3,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 150,
                tolerance: 3,
            },
        }),
        useSensor(KeyboardSensor)
    );

    const handleDragStart = useCallback((event) => {
        const { active } = event;
        const courseData = active.data.current;

        setActiveId(active.id);
        setActiveCourse(courseData);

        // Show virtual alternatives for section swapping
        if (courseData && !courseData.isSingleton) {
            showVirtualCourses(courseData.code, courseData.section);
        }
    }, [showVirtualCourses]);

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;

        setActiveId(null);
        setActiveCourse(null);
        hideVirtualCourses();

        if (!active?.data?.current || !over?.data?.current) return;

        const draggedData = active.data.current;
        const targetData = over.data.current;

        if (isValidSectionSwap(draggedData, targetData)) {
            swapSection(draggedData.code, draggedData.section, targetData.section);
        }
    }, [swapSection, hideVirtualCourses]);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        setActiveCourse(null);
        hideVirtualCourses();
    }, [hideVirtualCourses]);

    return (
        <ReactTimetableContext.Provider value={timetableState}>
            <DndContext
                sensors={sensors}
                measuring={measuring}
                collisionDetection={detectSwapCollision}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
            >
                {children}
                <DragOverlay dropAnimation={null}>
                    {activeId && activeCourse ? (
                        <CourseBoxOverlay course={activeCourse} />
                    ) : null}
                </DragOverlay>
            </DndContext>
        </ReactTimetableContext.Provider>
    );
});

DndTimetableProvider.displayName = 'DndTimetableProvider';

export default DndTimetableProvider;
